// Full-screen placeholder for a rewarded video ad (Open Beta / preview mode).

import React, { useEffect, useRef, useState } from "react";
import { REWARDED_AD_PREVIEW_DURATION_SEC } from "../utils/rewardedAdPreview";

const RewardedAdPreviewModal = ({
    open = false,
    rewardLabel = "+1 Heart",
    onComplete,
    onCancel,
    durationSec = REWARDED_AD_PREVIEW_DURATION_SEC,
}) => {
    const [secondsLeft, setSecondsLeft] = useState(durationSec);
    const completedRef = useRef(false);
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        if (!open) {
            completedRef.current = false;
            setSecondsLeft(durationSec);
            return undefined;
        }

        completedRef.current = false;
        setSecondsLeft(durationSec);
        const startedAt = Date.now();
        const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            const next = Math.max(0, durationSec - elapsed);
            setSecondsLeft(next);
            if (next <= 0 && !completedRef.current) {
                completedRef.current = true;
                clearInterval(timer);
                if (typeof onCompleteRef.current === "function") {
                    onCompleteRef.current();
                }
            }
        }, 200);

        return () => clearInterval(timer);
    }, [open, durationSec]);

    if (!open) {
        return null;
    }

    const progress =
        durationSec > 0
            ? Math.min(
                  100,
                  Math.round(((durationSec - secondsLeft) / durationSec) * 100)
              )
            : 100;
    const canClaim = secondsLeft <= 0;

    return (
        <div
            className="rewardedAdPreview"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rewardedAdPreviewTitle"
        >
            <div className="rewardedAdPreview__frame">
                <div className="rewardedAdPreview__topBar">
                    <span className="rewardedAdPreview__adBadge">Ad</span>
                    <span className="rewardedAdPreview__countdown">
                        {canClaim
                            ? "Reward unlocked"
                            : `Reward in ${secondsLeft}s`}
                    </span>
                </div>

                <div className="rewardedAdPreview__creative" aria-hidden="true">
                    <div className="rewardedAdPreview__creativeInner">
                        <div className="rewardedAdPreview__brand">Sponsored</div>
                        <div
                            id="rewardedAdPreviewTitle"
                            className="rewardedAdPreview__headline"
                        >
                            Memory Mix-Up — Open Beta
                        </div>
                        <p className="rewardedAdPreview__copy">
                            This is a preview of where a rewarded video would
                            play. Real ads arrive with AdMob later.
                        </p>
                    </div>
                </div>

                <div
                    className="rewardedAdPreview__progressTrack"
                    aria-hidden="true"
                >
                    <div
                        className="rewardedAdPreview__progressFill"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <div className="rewardedAdPreview__footer">
                    <p className="rewardedAdPreview__rewardHint">
                        {canClaim
                            ? `Earned ${rewardLabel}`
                            : `Watch to earn ${rewardLabel}`}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RewardedAdPreviewModal;
